import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { chatPendingChangesApi } from '@/api/chatPendingChanges'
import type { ChatPendingChangeSetItem } from '@/api/chatPendingChanges'
import { getErrorMessage, shouldNotRetryRequest } from '@/api/errors'
import { DEFAULT_QUERY_RETRY_COUNT } from '@/lib/queryClient'
import { useTenantKey } from './useTenantKey'

export function useChatPendingChanges(projectId: string, chatId: string | null) {
  const tenantKey = useTenantKey()
  return useQuery({
    queryKey: tenantKey('projects', projectId, 'chats', chatId, 'pending-changes'),
    queryFn: () => chatPendingChangesApi.get(projectId, chatId!),
    enabled: !!projectId && !!chatId,
    retry: (failureCount, error) => !shouldNotRetryRequest(error) && failureCount < DEFAULT_QUERY_RETRY_COUNT,
    refetchInterval: (query) => (shouldNotRetryRequest(query.state.error) ? false : 4_000),
  })
}

export function useSubmitChatPendingChanges(projectId: string, chatId: string | null) {
  const queryClient = useQueryClient()
  const tenantKey = useTenantKey()
  return useMutation({
    mutationFn: ({ confirmed, excluded }: { confirmed: ChatPendingChangeSetItem[]; excluded: ChatPendingChangeSetItem[] }) =>
      chatPendingChangesApi.submit(
        projectId,
        chatId!,
        confirmed.map((i) => i.id),
        excluded.map((i) => i.id),
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: tenantKey('projects', projectId, 'chats', chatId, 'pending-changes') })
      queryClient.invalidateQueries({ queryKey: tenantKey('projects', projectId, 'chats') })
      queryClient.invalidateQueries({ queryKey: tenantKey('projects', projectId, 'chats', chatId, 'messages') })
      queryClient.invalidateQueries({ queryKey: tenantKey('projects', projectId, 'work-items') })
      queryClient.invalidateQueries({ queryKey: tenantKey('projects', projectId, 'work-items', 'stats') })
    },
    onError: (error) => {
      toast.error(getErrorMessage(error) ?? 'Could not submit these changes. Please try again.')
    },
  })
}

export function useCancelChatPendingChanges(projectId: string, chatId: string | null) {
  const queryClient = useQueryClient()
  const tenantKey = useTenantKey()
  return useMutation({
    mutationFn: () => chatPendingChangesApi.cancel(projectId, chatId!),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: tenantKey('projects', projectId, 'chats', chatId, 'pending-changes') })
      queryClient.invalidateQueries({ queryKey: tenantKey('projects', projectId, 'chats') })
    },
    onError: (error) => {
      toast.error(getErrorMessage(error) ?? 'Could not cancel these changes. Please try again.')
    },
  })
}

export function useRemoveChatPendingChangeItem(projectId: string, chatId: string | null) {
  const queryClient = useQueryClient()
  const tenantKey = useTenantKey()
  return useMutation({
    mutationFn: (itemId: string) => chatPendingChangesApi.removeItem(projectId, chatId!, itemId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: tenantKey('projects', projectId, 'chats', chatId, 'pending-changes') })
      queryClient.invalidateQueries({ queryKey: tenantKey('projects', projectId, 'chats') })
    },
    onError: (error) => {
      toast.error(getErrorMessage(error) ?? 'Could not remove this item. Please try again.')
    },
  })
}
